import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Card,
    CardContent,
    Grid,
    Chip,
    CircularProgress,
    Alert,
    Autocomplete,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    Stack,
    ToggleButton,
    ToggleButtonGroup
} from '@mui/material';
// Using standard HTML5 date input instead of @mui/x-date-pickers
import {
    Print as PrintIcon,
    Download as DownloadIcon,
    Refresh as RefreshIcon,
    CheckCircle as CheckCircleIcon,
    CalendarToday as CalendarIcon,
    List as ListIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import DashboardLayout from '../../components/layouts/DashboardLayout';
import apiClient from '../../services/apiClient';
import pdfService from '../../services/pdfService';

const DeliveryRunSheet = () => {
    const [loading, setLoading] = useState(false);
    const [deliveryDate, setDeliveryDate] = useState(format(new Date(), 'yyyy-MM-dd'));
    const [runSheetData, setRunSheetData] = useState(null);
    const [routeFrom, setRouteFrom] = useState('');
    const [routeTo, setRouteTo] = useState('');
    const [awbInput, setAwbInput] = useState('');
    const [awbOptions, setAwbOptions] = useState([]);
    const [selectedAwbs, setSelectedAwbs] = useState([]);
    const [branchName, setBranchName] = useState('');
    const [areaLocator, setAreaLocator] = useState('');
    const [staffName, setStaffName] = useState('');
    const [driverName, setDriverName] = useState('');
    const [vehicleNo, setVehicleNo] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [generationMode, setGenerationMode] = useState('manual'); // 'manual' or 'date'

    // Fetch AWB options as user types (debounced via simple timeout)
    useEffect(() => {
        let active = true;
        const t = setTimeout(async () => {
            const term = awbInput?.trim();
            if (!term || term.length < 2) {
                if (active) setAwbOptions([]);
                return;
            }
            try {
                const res = await apiClient.get('/tracking/search', { params: { search: term } });
                if (active && res.data?.success) {
                    // Backend now handles all exclusions (delivered, in DRS, etc.)
                    const raw = res.data.data || [];
                    const opts = raw.map((s) => ({ awb_no: s.awb_no }));
                    setAwbOptions(opts);
                }
            } catch (e) {
                // ignore
            }
        }, 300);
        return () => { active = false; clearTimeout(t); };
    }, [awbInput]);

    const generateRunSheet = async () => {
        setLoading(true);
        setError('');
        setSuccess('');



        try {
            let params = {
                delivery_date: deliveryDate,
                staff_name: staffName.trim(),
                driver_name: driverName.trim(),
                vehicle_no: vehicleNo.trim(),
                branch_name: branchName.trim() || null,
                area_locator: areaLocator.trim() || null,
                route_from: null,
                route_to: null,
                franchise_id: null,
                awb_numbers: [],
                is_manual_selection: generationMode === 'manual'
            };

            if (generationMode === 'date') {
                // Date-based generation - we need to get the AWBs first
                const generateResponse = await apiClient.post('/delivery-run-sheet/generate', {
                    delivery_date: deliveryDate,
                    branch_name: branchName.trim() || null,
                    area_locator: areaLocator.trim() || null
                });
                
                if (generateResponse.data.success) {
                    params.awb_numbers = generateResponse.data.data.bookings.map(b => b.awb_no);
                } else {
                    setError('No bookings found for the selected date and filters');
                    setLoading(false);
                    return;
                }
            } else {
                // Manual AWB selection
                params.awb_numbers = selectedAwbs.map(o => o.awb_no);
            }

            if (params.awb_numbers.length === 0) {
                setError('No AWB numbers selected');
                setLoading(false);
                return;
            }

            console.log('Creating DRS with params:', params);
            const response = await apiClient.post('/drs/create', params);
            
            if (response.data.success) {
                // Get the created DRS details to show preview
                const drsDetailsResponse = await apiClient.get(`/drs/${response.data.data.drs_header.id}`);
                
                if (drsDetailsResponse.data.success) {
                    setRunSheetData(drsDetailsResponse.data.data);
                    setSuccess(`DRS ${response.data.data.drs_header.drs_no} created successfully with ${response.data.data.total_bookings} bookings`);
                } else {
                    setError('DRS created but failed to load details');
                }
            } else {
                setError(response.data.message || 'Failed to create DRS');
            }
        } catch (error) {
            console.error('Generate DRS error:', error);
            
            // Handle different types of errors
            if (error.response && error.response.data) {
                const errorData = error.response.data;
                
                // Handle validation errors
                if (errorData.errors) {
                    const errorMessages = [];
                    Object.keys(errorData.errors).forEach(key => {
                        errorData.errors[key].forEach(message => {
                            errorMessages.push(message);
                        });
                    });
                    setError(errorMessages.join(', '));
                } else if (errorData.message) {
                    setError(errorData.message);
                } else {
                    setError('Failed to create DRS. Please check your input and try again.');
                }
            } else if (error.message) {
                setError('Failed to create DRS: ' + error.message);
            } else {
                setError('Failed to create DRS. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };



    // Removed save run sheet functionality as per requirement

    const printRunSheet = async () => {
        if (!runSheetData || !runSheetData.bookings.length) {
            setError('No run sheet data to print');
            return;
        }
        try {
            setLoading(true);
            await pdfService.generateAndPrintDeliveryRunSheet(runSheetData);
            setSuccess('Print preview opened successfully');
        } catch (error) {
            setError('Failed to generate print preview: ' + error.message);
            console.error('PDF print error:', error);
        } finally {
            setLoading(false);
        }
    };

    const exportToPdf = async () => {
        if (!runSheetData || !runSheetData.bookings.length) {
            setError('No run sheet data to export');
            return;
        }

        try {
            setLoading(true);
            await pdfService.generateAndDownloadDeliveryRunSheet(runSheetData);
            setSuccess('PDF downloaded successfully');
        } catch (error) {
            setError('Failed to generate PDF: ' + error.message);
            console.error('PDF generation error:', error);
        } finally {
            setLoading(false);
        }
    };

    const resetFilters = () => {
        setRouteFrom('');
        setRouteTo('');
        setSelectedAwbs([]);
        setAwbInput('');
        setBranchName('');
        setAreaLocator('');
        setStaffName('');
        setDriverName('');
        setVehicleNo('');
        setRunSheetData(null);
        setError('');
        setSuccess('');
    };

    return (
        <DashboardLayout>
            <Box sx={{ p: 3 }}>
                    {/* Header */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h4" component="h1" gutterBottom>
                            Delivery Run Sheet (DRS)
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Generate and store Delivery Run Sheets (DRS) by selecting a date or manually adding AWB numbers. Optionally fill in DRS details and apply filters.
                        </Typography>
                    </Box>

                    {/* Filters Section */}
                    <Paper sx={{ p: 3, mb: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Generate DRS
                        </Typography>
                        
                        {/* Generation Mode Toggle */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Select generation mode:
                            </Typography>
                            <ToggleButtonGroup
                                value={generationMode}
                                exclusive
                                onChange={(event, newMode) => {
                                    if (newMode !== null) {
                                        setGenerationMode(newMode);
                                        // Reset relevant fields when switching modes
                                        if (newMode === 'date') {
                                            setSelectedAwbs([]);
                                            setAwbInput('');
                                        } else {
                                            setDeliveryDate(format(new Date(), 'yyyy-MM-dd'));
                                        }
                                    }
                                }}
                                aria-label="generation mode"
                            >
                                <ToggleButton value="date" aria-label="date based">
                                    <CalendarIcon sx={{ mr: 1 }} />
                                    Date Based
                                </ToggleButton>
                                <ToggleButton value="manual" aria-label="manual selection">
                                    <ListIcon sx={{ mr: 1 }} />
                                    Manual AWB Selection
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Box>

                        <Grid container spacing={3} alignItems="center">
                            {/* Date Selection - Only show when in date mode */}
                            {generationMode === 'date' && (
                                <Grid item xs={12} md={3}>
                                    <TextField
                                        label="Delivery Date"
                                        type="date"
                                        value={deliveryDate}
                                        onChange={(e) => setDeliveryDate(e.target.value)}
                                        fullWidth
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                    />
                                </Grid>
                            )}

                            {/* Branch Name */}
                            <Grid item xs={12} md={3}>
                                <TextField
                                    label="Branch Name"
                                    value={branchName}
                                    onChange={(e) => setBranchName(e.target.value)}
                                    fullWidth
                                />
                            </Grid>

                            {/* Area/Locator */}
                            <Grid item xs={12} md={3}>
                                <TextField
                                    label="Area/Locator"
                                    value={areaLocator}
                                    onChange={(e) => setAreaLocator(e.target.value)}
                                    fullWidth
                                />
                            </Grid>

                            {/* Staff Name */}
                            <Grid item xs={12} md={3}>
                                <TextField
                                    label="Staff Name"
                                    value={staffName}
                                    onChange={(e) => setStaffName(e.target.value)}
                                    fullWidth
                                />
                            </Grid>

                            {/* Driver Name */}
                            <Grid item xs={12} md={3}>
                                <TextField
                                    label="Driver Name"
                                    value={driverName}
                                    onChange={(e) => setDriverName(e.target.value)}
                                    fullWidth
                                />
                            </Grid>

                            {/* Vehicle Number */}
                            <Grid item xs={12} md={3}>
                                <TextField
                                    label="Vehicle Number"
                                    value={vehicleNo}
                                    onChange={(e) => setVehicleNo(e.target.value)}
                                    fullWidth
                                />
                            </Grid>
                            
                            {/* AWB Multi-select - Only show when in manual mode */}
                            {generationMode === 'manual' && (
                                <Grid item xs={12} md={6}>
                                    <Autocomplete
                                        multiple
                                        options={awbOptions}
                                        getOptionLabel={(option) => option?.awb_no || ''}
                                        value={selectedAwbs}
                                        onChange={(event, newValue) => {
                                            // Enforce max 50 and unique by awb_no
                                            const unique = [];
                                            const seen = new Set();
                                            for (const item of newValue) {
                                                if (!seen.has(item.awb_no)) {
                                                    seen.add(item.awb_no);
                                                    unique.push(item);
                                                }
                                                if (unique.length === 50) break;
                                            }
                                            setSelectedAwbs(unique);
                                        }}
                                        inputValue={awbInput}
                                        onInputChange={(event, newInputValue) => {
                                            setAwbInput(newInputValue);
                                        }}
                                        filterOptions={(options) => options.filter(o => !selectedAwbs.some(s => s.awb_no === o.awb_no))}
                                        filterSelectedOptions
                                        isOptionEqualToValue={(option, value) => option.awb_no === value.awb_no}
                                        onOpen={async () => {
                                            // No-op; options are loaded via effect below
                                        }}
                                        renderInput={(params) => (
                                            <TextField {...params} label={`AWB Numbers (max 50)`} placeholder="Type to search AWB" />
                                        )}
                                    />
                                </Grid>
                            )}

                            {/* Generate Button */}
                            <Grid item xs={12} md={generationMode === 'date' ? 3 : 6}>
                                <Stack direction="row" spacing={1}>
                                    <Button
                                        variant="contained"
                                        onClick={generateRunSheet}
                                        disabled={loading || 
                                            (generationMode === 'manual' && selectedAwbs.length === 0)}
                                        startIcon={loading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                                    >
                                        Generate DRS
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={resetFilters}
                                        disabled={loading}
                                    >
                                        Reset
                                    </Button>
                                </Stack>
                            </Grid>
                        </Grid>

                        {/* Mode-specific instructions */}
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                {generationMode === 'date' 
                                    ? 'Select a delivery date to generate DRS for all pending bookings on that date.'
                                    : 'Choose AWB numbers (max 50) and optionally fill in DRS details. Delivered AWBs and AWBs already in existing DRS are automatically excluded from suggestions.'
                                }
                            </Typography>
                        </Box>
                    </Paper>

                    {/* Messages */}
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}
                    
                    {success && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            {success}
                        </Alert>
                    )}

                    {/* Results Section */}
                    {runSheetData && (
                        <Paper sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6">
                                    {runSheetData.drs_header ? `DRS ${runSheetData.drs_header.drs_no}` : 'Delivery Run Sheet'} - {format(new Date(runSheetData.delivery_date), 'dd MMM yyyy')}
                                </Typography>
                                <Stack direction="row" spacing={1}>
                                    <Button
                                        variant="outlined"
                                        startIcon={<PrintIcon />}
                                        onClick={printRunSheet}
                                    >
                                        Print
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        startIcon={<DownloadIcon />}
                                        onClick={exportToPdf}
                                    >
                                        Export PDF
                                    </Button>
                                </Stack>
                            </Box>
                            {/* Summary Cards */}
                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                {runSheetData.drs_header && (
                                    <Grid item xs={12} md={3}>
                                        <Card>
                                            <CardContent>
                                                <Typography color="text.secondary" gutterBottom>
                                                    DRS Number
                                                </Typography>
                                                <Typography variant="h6" color="primary" fontWeight="bold">
                                                    {runSheetData.drs_header.drs_no}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                )}
                                <Grid item xs={12} md={3}>
                                    <Card>
                                        <CardContent>
                                            <Typography color="text.secondary" gutterBottom>
                                                Delivery Date
                                            </Typography>
                                            <Typography variant="h6">
                                                {format(new Date(runSheetData.delivery_date), 'dd MMM yyyy')}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <Card>
                                        <CardContent>
                                            <Typography color="text.secondary" gutterBottom>
                                                Branch Name
                                            </Typography>
                                            <Typography variant="h6">
                                                {runSheetData.branch_name && runSheetData.branch_name.trim() ? runSheetData.branch_name : '-'}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <Card>
                                        <CardContent>
                                            <Typography color="text.secondary" gutterBottom>
                                                Area/Locator
                                            </Typography>
                                            <Typography variant="h6">
                                                {runSheetData.area_locator && runSheetData.area_locator.trim() ? runSheetData.area_locator : '-'}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <Card>
                                        <CardContent>
                                            <Typography color="text.secondary" gutterBottom>
                                                Total Bookings
                                            </Typography>
                                            <Typography variant="h6">
                                                {runSheetData.total_bookings}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                {runSheetData.drs_header && (
                                    <>
                                        <Grid item xs={12} md={3}>
                                            <Card>
                                                <CardContent>
                                                    <Typography color="text.secondary" gutterBottom>
                                                        Staff Name
                                                    </Typography>
                                                    <Typography variant="h6">
                                                        {runSheetData.drs_header.staff_name}
                                                    </Typography>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <Card>
                                                <CardContent>
                                                    <Typography color="text.secondary" gutterBottom>
                                                        Driver Name
                                                    </Typography>
                                                    <Typography variant="h6">
                                                        {runSheetData.drs_header.driver_name}
                                                    </Typography>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <Card>
                                                <CardContent>
                                                    <Typography color="text.secondary" gutterBottom>
                                                        Vehicle No
                                                    </Typography>
                                                    <Typography variant="h6">
                                                        {runSheetData.drs_header.vehicle_no}
                                                    </Typography>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    </>
                                )}
                            </Grid>

                            {/* Delivery Run Sheet Table */}
                            
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                            <TableRow>
                                                <TableCell>SI.NO</TableCell>
                                                <TableCell>AWB No & Barcode</TableCell>
                                                <TableCell>Consignor</TableCell>
                                                <TableCell>Consignee</TableCell>
                                                <TableCell>Packages</TableCell>
                                                <TableCell>Weight (kg)</TableCell>
                                                <TableCell>Seal & Sign</TableCell>
                                            </TableRow>
                                        </TableHead>
                                    <TableBody>
                                        {runSheetData.bookings.map((booking) => (
                                            <TableRow key={booking.awb_no}>
                                                <TableCell>{booking.si_no}</TableCell>
                                                <TableCell>
                                                    <Box sx={{ textAlign: 'center' }}>
                                                        <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
                                                            {booking.awb_no}
                                                        </Typography>
                                                        {booking.booking_qr && (
                                                            <img 
                                                                src={`data:image/png;base64,${booking.booking_qr}`}
                                                                alt={`Barcode for ${booking.awb_no}`}
                                                                style={{ 
                                                                    width: '120px', 
                                                                    height: '40px',
                                                                    border: '1px solid #ddd',
                                                                    borderRadius: '2px'
                                                                }}
                                                            />
                                                        )}
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Box>
                                                        <Typography variant="body2" fontWeight="bold">
                                                            {booking.from_customer}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {booking.from_city}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Box>
                                                        <Typography variant="body2" fontWeight="bold">
                                                            {booking.to_customer}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {booking.to_city}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>{booking.no_of_packages}</TableCell>
                                                <TableCell>{booking.weight}</TableCell>
                                                <TableCell>
                                                    <Box sx={{ 
                                                        border: '1px dashed #ccc', 
                                                        minHeight: '40px',
                                                        borderRadius: 1,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        minWidth: '100px'
                                                    }}>
                                                        {booking.seal_sign_status ? (
                                                            <CheckCircleIcon color="success" />
                                                        ) : (
                                                            <Typography variant="caption" color="text.secondary">
                                                                Signature Area
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    )}


                </Box>
        </DashboardLayout>
    );
};

export default DeliveryRunSheet; 